"use client";

import { Button, IconButton } from "../../shared";
import { Box, Card, Divider, Sheet, Typography, useTheme } from "@mui/joy";
import { Eclipse, Ellipsis, ListTodo, Maximize2, Share, Share2, X } from "lucide-react";
import { createPortal } from "react-dom";

export default function SideModal({
    open,
    onClose,
    children,
    portalBoxProps = {},
    showAdditionalHeadersIcons = true,
    title = "",
    description = "",
}: ISideModal) {
    const theme = useTheme(); // Get current theme

    if (!open) return null; // Don't render if closed

    return createPortal(
        <Box
            sx={{
                position: "fixed",
                top: 0,
                right: 0,
                width: "700px", // Adjust modal width
                height: "100vh",
                zIndex: 1010, // Ensure it's on top
                ...portalBoxProps,
            }}
        >
            {/* Side Panel */}
            <Card
                sx={{
                    position: "relative",
                    height: "100%",
                    width: "100%",
                    border: "none",
                    gap: 0,
                    p: 0,
                    borderRadius: 0, // No rounded corners
                    // backgroundColor:
                    //     theme.palette.mode === "dark"
                    //         ? "rgba(20, 20, 20, 0.7)" // Dark frosted effect
                    //         : "rgba(255, 255, 255, 0.6)", // Light frosted effect
                    backdropFilter: "blur(10px)", // Frosted glass effect inside modal
                    WebkitBackdropFilter: "blur(10px)", // Safari support
                    boxShadow: "0px 0px 78px rgba(0, 0, 0, 0.3)", // Left-side shadow
                    color: theme.palette.mode === "dark" ? "#fff" : "#000", // Ensure readable text
                    overflow: "auto", // Allow scrolling if content overflows
                }}
            >
                <Sheet sx={{ position: "sticky", top: 0, pt: 1.5, zIndex: 1001 }}>
                    <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
                        {title && (
                            <Box display={"flex"} alignItems={"center"} sx={{ ml: 2 }} gap={1.5}>
                                <Card sx={{ p: 0.7 }}>
                                    <ListTodo size={20} />
                                </Card>

                                <Box>
                                    <Typography level="title-lg">{title}</Typography>
                                    <Typography level="body-sm">{description} </Typography>
                                </Box>
                            </Box>
                        )}
                        {showAdditionalHeadersIcons && (
                            <Box display={"flex"} pl={1} gap={1} alignItems={"center"}>
                                <Button startDecorator={<Share2 size={18} />} variant={"plain"}>
                                    Share
                                </Button>
                                <Button startDecorator={<Maximize2 size={18} />} variant={"plain"}>
                                    Expand
                                </Button>
                                <IconButton variant={"plain"}>
                                    <Ellipsis />
                                </IconButton>
                            </Box>
                        )}
                        <IconButton variant={"plain"} sx={{ ml: 2, mr: 1.5 }} onClick={onClose}>
                            <X />
                        </IconButton>
                    </Box>
                    <Divider inset="context" sx={{ mt: 1.5 }} />
                </Sheet>
                {children}
            </Card>
        </Box>,
        document.getElementById("layout-root") || document.body // Moves it outside of all stacking contexts
    );
}

interface ISideModal {
    open: boolean;
    onClose: () => void;
    children: React.ReactNode;
    portalBoxProps?: any;
    showAdditionalHeadersIcons?: boolean;
    title?: string;
    description?: string;
}
